/*
 * Display-only currency converter.
 *
 * Rewrites prices formatted in the shop currency (e.g. "Rs.2,399.00") into the
 * visitor's chosen currency. Checkout is untouched and always charges in the
 * shop currency. Loaded in <head> so prices are converted as the page parses.
 */
(function () {
  const configElement = document.getElementById('currency-config');
  if (!configElement) return;

  const config = JSON.parse(configElement.textContent);
  const BASE = config.base;
  const CHOICE_KEY = 'emraz-currency';
  const DETECTED_KEY = 'emraz-currency-detected';
  const RATES_KEY = 'emraz-currency-rates';
  const RATES_MAX_AGE = 12 * 60 * 60 * 1000;
  const COUNTRY_CURRENCY = { AE: 'AED', US: 'USD' };
  const SKIP_SELECTOR =
    'script, style, noscript, template, textarea, input, select, [data-currency-ignore], .price-facet__highest-price';

  /* Stored values may be blocked (private mode), so every access is guarded */
  const storage = {
    get(key) {
      try {
        return localStorage.getItem(key);
      } catch (error) {
        return null;
      }
    },
    set(key, value) {
      try {
        localStorage.setItem(key, value);
      } catch (error) {
        /* ignore */
      }
    },
  };

  /* PKR per one unit of each currency */
  const rates = { [BASE]: 1, ...config.rates };

  function isSupported(code) {
    return Object.prototype.hasOwnProperty.call(rates, code);
  }

  /* Live rates, cached for 12 hours; ignored if they stray far from the backup rates */
  function applyRates(fetched) {
    Object.keys(config.rates).forEach((code) => {
      const live = fetched[code];
      const backup = config.rates[code];
      if (live > backup * 0.6 && live < backup * 1.4) rates[code] = live;
    });
  }

  function loadRates() {
    if (!config.liveRates) return;

    let cached = null;
    try {
      cached = JSON.parse(storage.get(RATES_KEY));
    } catch (error) {
      cached = null;
    }
    if (cached && cached.rates) applyRates(cached.rates);
    if (cached && Date.now() - cached.time < RATES_MAX_AGE) return;

    fetch('https://open.er-api.com/v6/latest/' + BASE)
      .then((response) => response.json())
      .then((data) => {
        if (data.result !== 'success') return;
        const fetched = {};
        Object.keys(config.rates).forEach((code) => {
          if (data.rates[code]) fetched[code] = 1 / data.rates[code];
        });
        storage.set(RATES_KEY, JSON.stringify({ time: Date.now(), rates: fetched }));
        applyRates(fetched);
        if (current !== BASE) convertAll();
      })
      .catch(() => {});
  }

  /* Price matching, built from the shop's money format, e.g. "Rs.{{amount}}" */
  function escapeRegExp(text) {
    return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  const formatMatch = config.moneyFormat.match(/\{\{\s*(\w+)\s*\}\}/);
  const placeholder = formatMatch ? formatMatch[1] : 'amount';
  const [formatPrefix, formatSuffix] = config.moneyFormat.split(formatMatch ? formatMatch[0] : '{{amount}}');
  const commaDecimal = /comma_separator|apostrophe/.test(placeholder) && !/no_decimals/.test(placeholder);
  const amountPattern = commaDecimal ? "[\\d.']*\\d(?:,\\d+)?" : "[\\d,']*\\d(?:\\.\\d+)?";
  const pricePattern = new RegExp(
    escapeRegExp(formatPrefix.trim()) +
      '\\s?(' +
      amountPattern +
      ')' +
      escapeRegExp(formatSuffix) +
      '(?:\\s*' +
      escapeRegExp(BASE) +
      ')?',
    'g'
  );

  function parseAmount(text) {
    const digits = commaDecimal ? text.replace(/[.']/g, '').replace(',', '.') : text.replace(/[,']/g, '');
    return parseFloat(digits);
  }

  const formatters = {};
  function formatAmount(value, code) {
    if (!formatters[code]) {
      formatters[code] = new Intl.NumberFormat('en', {
        style: 'currency',
        currency: code,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    }
    return formatters[code].format(value);
  }

  function convertText(text) {
    return text.replace(pricePattern, (match, amount) => {
      const value = parseAmount(amount);
      if (Number.isNaN(value)) return match;
      return formatAmount(value / rates[current], current);
    });
  }

  /* Text nodes we have touched: their original text and what we wrote */
  const touched = new WeakMap();

  function shouldSkip(node) {
    const parent = node.parentElement;
    return !parent || parent.closest(SKIP_SELECTOR) !== null;
  }

  function processNode(node) {
    let record = touched.get(node);

    if (!record || node.data !== record.output) {
      /* New node, or the theme replaced its text with a fresh price */
      pricePattern.lastIndex = 0;
      if (!pricePattern.test(node.data)) {
        touched.delete(node);
        return;
      }
      if (shouldSkip(node)) return;
      record = { original: node.data, output: node.data };
      touched.set(node, record);
    }

    pricePattern.lastIndex = 0;
    const output = current === BASE ? record.original : convertText(record.original);
    record.output = output;
    if (node.data !== output) node.data = output;
  }

  /*
   * While the page is still parsing, a text node can arrive in pieces. Hold it
   * until the parser has moved past it so a half-read price is never converted.
   */
  const pending = new Set();

  function isSettled(node) {
    return document.readyState !== 'loading' || node.nextSibling || (node.parentNode && node.parentNode.nextSibling);
  }

  function visitText(node) {
    if (isSettled(node)) {
      processNode(node);
    } else {
      pending.add(node);
    }
  }

  function processTree(root) {
    if (root.nodeType === Node.TEXT_NODE) {
      visitText(root);
      return;
    }
    if (root.nodeType !== Node.ELEMENT_NODE && root.nodeType !== Node.DOCUMENT_NODE) return;

    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let node = walker.nextNode();
    while (node) {
      visitText(node);
      node = walker.nextNode();
    }
  }

  function flushPending() {
    pending.forEach((node) => {
      if (!isSettled(node)) return;
      pending.delete(node);
      processNode(node);
    });
  }

  function convertAll() {
    processTree(document.body || document.documentElement);
  }

  /* Convert prices as the page parses and whenever the theme swaps in new HTML */
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === 'characterData') {
        visitText(mutation.target);
      } else {
        mutation.addedNodes.forEach(processTree);
      }
    }
    if (pending.size) flushPending();
  });

  document.addEventListener('DOMContentLoaded', () => {
    flushPending();
    convertAll();
  });

  /* Currency choice: the visitor's own pick, else their location, else the shop currency */
  let current = BASE;

  function setCurrency(code, { remember = false } = {}) {
    if (!isSupported(code)) code = BASE;
    if (remember) storage.set(CHOICE_KEY, code);
    const changed = code !== current;
    current = code;
    document.documentElement.dataset.currency = code;
    document.documentElement.toggleAttribute('data-currency-converted', code !== BASE);
    if (changed) {
      convertAll();
      document.dispatchEvent(new CustomEvent('currency:change', { detail: { currency: code } }));
    }
  }

  function detectCurrency() {
    if (!config.autoDetect || storage.get(CHOICE_KEY)) return;

    const detected = storage.get(DETECTED_KEY);
    if (detected) {
      setCurrency(detected);
      return;
    }

    fetch(config.geoUrl, { headers: { Accept: 'application/json' } })
      .then((response) => response.json())
      .then((data) => {
        const country = data && data.detected_values && data.detected_values.country;
        const code = (country && COUNTRY_CURRENCY[country.handle]) || BASE;
        storage.set(DETECTED_KEY, isSupported(code) ? code : BASE);
        if (!storage.get(CHOICE_KEY)) setCurrency(code);
      })
      .catch(() => {});
  }

  const chosen = storage.get(CHOICE_KEY);
  setCurrency(chosen && isSupported(chosen) ? chosen : storage.get(DETECTED_KEY) || BASE);
  observer.observe(document.documentElement, { childList: true, subtree: true, characterData: true });
  loadRates();
  detectCurrency();

  window.EmrazCurrency = {
    get current() {
      return current;
    },
    set: (code) => setCurrency(code, { remember: true }),
  };

  /* The switcher button and its menu */
  /*
   * Defined before the page body parses, so children may not exist yet when it
   * connects: listeners are delegated and children are looked up on demand.
   */
  class CurrencySwitcher extends HTMLElement {
    constructor() {
      super();
      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onDocumentClick = (event) => {
        if (!this.contains(event.target)) this.close();
      };
      this.onCurrencyChange = () => this.render();
    }

    get button() {
      return this.querySelector('.currency-switcher__button');
    }

    get panel() {
      return this.querySelector('.currency-switcher__panel');
    }

    connectedCallback() {
      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeydown);
      document.addEventListener('currency:change', this.onCurrencyChange);
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', this.onCurrencyChange, { once: true });
      } else {
        this.render();
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('currency:change', this.onCurrencyChange);
      document.removeEventListener('click', this.onDocumentClick);
    }

    onClick(event) {
      const option = event.target.closest('[data-currency-option]');
      if (option) {
        window.EmrazCurrency.set(option.dataset.currencyOption);
        this.close();
        this.button?.focus();
      } else if (event.target.closest('.currency-switcher__button')) {
        this.toggle();
      }
    }

    onKeydown(event) {
      if (event.key === 'Escape' && this.hasAttribute('open')) {
        this.close();
        this.button?.focus();
      }
    }

    render() {
      const label = this.querySelector('.currency-switcher__current');
      if (label) label.textContent = current;
      this.querySelectorAll('[data-currency-option]').forEach((option) => {
        option.setAttribute('aria-pressed', String(option.dataset.currencyOption === current));
      });
    }

    toggle() {
      if (this.hasAttribute('open')) {
        this.close();
      } else {
        this.open();
      }
    }

    open() {
      if (!this.panel) return;
      this.setAttribute('open', '');
      this.panel.hidden = false;
      this.button.setAttribute('aria-expanded', 'true');
      document.addEventListener('click', this.onDocumentClick);
    }

    close() {
      this.removeAttribute('open');
      if (this.panel) this.panel.hidden = true;
      this.button?.setAttribute('aria-expanded', 'false');
      document.removeEventListener('click', this.onDocumentClick);
    }
  }

  if (!customElements.get('currency-switcher')) customElements.define('currency-switcher', CurrencySwitcher);
})();
